import { addCache } from 'api/caching'
import { orgScopedApiClient } from 'api/orgScopedClient'
import {
    CurrencyCode,
    Dashboard,
    DashboardTemplate,
    DashboardTemplateTag,
    ScheduledExport,
} from 'types'

import { getCerebroHeaders } from '../headers'
import {
    CerebroApiOptionsHeaders,
    CerebroPaginatedResponse,
    CerebroResourceResponse,
} from '../types'

export const getDashboards = (
    params: Record<string, unknown>
): Promise<CerebroPaginatedResponse<Dashboard>> =>
    orgScopedApiClient.get('/api/dashboards/', {
        params,
    })
addCache(getDashboards)

export const getDashboard = (
    dashboardId: string
): Promise<CerebroResourceResponse<Dashboard>> =>
    orgScopedApiClient.get(`/api/dashboard/${dashboardId}/`)

export const createDashboard = (
    data: Pick<Dashboard, 'name' | 'description' | 'config'>
): Promise<CerebroResourceResponse<Dashboard>> =>
    orgScopedApiClient.post('/api/dashboards/', data)

export const updateDashboard = (
    dashboardId: string,
    data: Record<string, unknown>
): Promise<CerebroResourceResponse<Dashboard>> =>
    orgScopedApiClient.patch(`/api/dashboard/${dashboardId}/`, data)

export const deleteDashboard = (
    dashboardId: string
): Promise<CerebroResourceResponse<void>> =>
    orgScopedApiClient.delete(`/api/dashboard/${dashboardId}/`)

export const exportDashboardToPdf = (
    dashboardId: string,
    params: {
        async_download_name: string
        currency_code: CurrencyCode
        date_range: string
    }
): Promise<CerebroResourceResponse<void>> =>
    orgScopedApiClient.post(
        `/api/dashboard/${dashboardId}/export/pdf/`,
        {},
        { params }
    )

export const createScheduledExport = (
    data: Record<string, unknown>,
    params: Record<string, unknown>
): Promise<CerebroResourceResponse<ScheduledExport>> =>
    orgScopedApiClient.post('/api/export_schedules/', data, { params })

export const updateScheduledExport = (
    id: string,
    data: Record<string, unknown>,
    params: Record<string, unknown>
): Promise<CerebroResourceResponse<ScheduledExport>> =>
    orgScopedApiClient.patch(`/api/export_schedule/${id}/`, data, {
        params,
    })

export const deleteScheduledExport = (
    id: string
): Promise<CerebroResourceResponse<void>> =>
    orgScopedApiClient.delete(`/api/export_schedule/${id}/`)

export const getScheduledExports = (
    params: Record<string, unknown>
): Promise<CerebroPaginatedResponse<ScheduledExport>> =>
    orgScopedApiClient.get('/api/export_schedules/', { params })

export interface GetDashboardTemplatesRequestParams {
    limit: number
    tags__id?: string
}

export const getDashboardTemplates = (
    params: GetDashboardTemplatesRequestParams,
    options: { headers: CerebroApiOptionsHeaders } = { headers: {} }
): Promise<CerebroPaginatedResponse<DashboardTemplate>> =>
    orgScopedApiClient.get('/api/dashboard_templates/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getDashboardTemplatesTags = (
    params: Record<string, unknown>,
    options: { headers: CerebroApiOptionsHeaders } = { headers: {} }
): Promise<CerebroPaginatedResponse<DashboardTemplateTag>> =>
    orgScopedApiClient.get('/api/dashboard_templates/tags/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getDashboardTemplate = (
    dashboardTemplateId: string,
    params: Record<string, unknown> = {},
    options: { headers: CerebroApiOptionsHeaders } = { headers: {} }
): Promise<CerebroResourceResponse<DashboardTemplate>> =>
    orgScopedApiClient.get(`/api/dashboard_template/${dashboardTemplateId}/`, {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const createDashboardFromTemplate = (
    dashboardTemplateId: string,
    params: Record<string, unknown> = {},
    options: { headers: CerebroApiOptionsHeaders } = { headers: {} }
): Promise<CerebroResourceResponse<Dashboard>> => {
    return orgScopedApiClient.post(
        `/api/dashboard_template/${dashboardTemplateId}/dashboards/`,
        {
            params,
            headers: getCerebroHeaders(options.headers),
        }
    )
}
